<template>
    <div class="weeduc-admin-page course-group-form-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="editingId ? t.groups.formTitleEdit : t.groups.formTitleCreate"
                    :subtitle="editingId ? t.groups.formSubtitleEdit : t.groups.formSubtitleCreate"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn
                    v-if="!editingId"
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    prepend-icon="mdi-auto-fix"
                    @click="fillDemoData"
                >
                    {{ t.groups.fillDemoData }}
                </v-btn>
                <v-btn variant="text" class="text-none" @click="goBack">
                    {{ t.common.cancel }}
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    :loading="saving"
                    @click="save"
                >
                    {{ t.common.save }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div v-if="loading" class="course-group-form-placeholder">
                <Loader :isActive="false" />
            </div>
            <div v-else class="weeduc-admin-page__panel">
                <div class="corporate-groups-form">
                    <section class="corporate-dialog__section">
                        <p class="corporate-dialog__section-label">{{ t.groups.sectionData }}</p>
                        <div class="corporate-dialog__section-body">
                            <GroupBasicProductFields v-model="form" />
                            <div class="corporate-dialog__row">
                                <v-select
                                    v-model="form.mode"
                                    :items="modeOptions"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.groups.mode"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                            </div>
                            <p class="corporate-dialog__hint">{{ t.groups.modeHint }}</p>
                            <p
                                v-if="form.mode === 'path'"
                                class="corporate-dialog__hint corporate-groups-form__path-hint"
                            >
                                {{ t.groups.pathOrderHint }}
                            </p>
                            <div class="corporate-groups-form__access-banner">
                                <p class="corporate-dialog__hint">{{ t.groups.accessHint }}</p>
                                <v-btn
                                    size="small"
                                    variant="tonal"
                                    color="primary"
                                    rounded="lg"
                                    class="text-none"
                                    @click="goToLiberacao"
                                >
                                    {{ t.groups.accessLink }}
                                </v-btn>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import GroupBasicProductFields from '@/components/Admin/GroupBasicProductFields.vue';
import {
    emptyGroupProductFields,
    mapGroupProductFromRow,
    toGroupProductPayload,
    buildDemoGroupProductFields,
    createDemoCoverFile,
} from '@/utils/groupProductFields';
import { corporateI18n } from '@/i18n/mixins';
import courseGroupsApi from '@/utils/corporate/courseGroups';
import apiAWS from '@/utils/storage/aws';

const emptyForm = () => ({
    ...emptyGroupProductFields(),
    mode: 'group',
    target: { sectorIds: [], roleIds: [] },
    items: [],
});

export default {
    name: 'CourseGroupForm',
    mixins: [corporateI18n],
    components: { TitlePage, Loader, GroupBasicProductFields },
    props: {
        groupId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            form: emptyForm(),
        };
    },
    computed: {
        ...mapState(['token']),
        editingId() {
            return this.groupId || this.$route.query.id || null;
        },
        modeOptions() {
            return [
                { text: this.t.groups.modeGroup, value: 'group' },
                { text: this.t.groups.modePath, value: 'path' },
            ];
        },
    },
    watch: {
        editingId: {
            immediate: true,
            handler() {
                this.bootstrap();
            },
        },
    },
    methods: {
        async bootstrap() {
            this.loading = true;
            try {
                if (this.editingId) {
                    await this.loadGroup();
                } else {
                    this.form = emptyForm();
                }
            } finally {
                this.loading = false;
            }
        },
        async loadGroup() {
            try {
                const row = await courseGroupsApi.getById(this.editingId, this.token);
                const items = (row.items || []).map((item) => ({
                    courseId: item.courseId != null ? String(item.courseId) : null,
                    order: item.order,
                }));
                this.form = {
                    ...mapGroupProductFromRow(row),
                    mode: row.mode || 'group',
                    target: { sectorIds: [], roleIds: [] },
                    items,
                };
            } catch (e) {
                alert(this.t.common.error);
                this.goBack();
            }
        },
        async resolveCoverImageUrl() {
            if (this.form.coverFile instanceof File) {
                try {
                    const signature = await apiAWS.getSignatureImage(
                        this.token,
                        this.form.coverFile.name,
                    );
                    await apiAWS.putFileAWS(signature.url, this.form.coverFile);
                    return signature.token;
                } catch (e) {
                    return this.form.imageUrl || URL.createObjectURL(this.form.coverFile);
                }
            }
            return this.form.imageUrl || null;
        },
        goBack() {
            if (this.editingId) {
                this.$router.push(`/dashboard/grupo/${this.editingId}`);
                return;
            }
            this.$router.push('/dashboard/cursos');
        },
        goToDetail(groupId) {
            this.$router.push(`/dashboard/grupo/${groupId}`);
        },
        goToLiberacao() {
            this.$router.push('/dashboard/corporativo/liberacoes');
        },
        async fillDemoData() {
            const demo = buildDemoGroupProductFields();
            this.form = {
                ...this.form,
                ...demo,
                mode: this.form.mode || 'group',
                target: { sectorIds: [], roleIds: [] },
                items: this.form.items || [],
            };
            try {
                const coverFile = await createDemoCoverFile(
                    `${demo.name} capa`,
                    'pacote-capa-demo.png',
                );
                this.form = {
                    ...this.form,
                    coverFile,
                    imageUrl: '',
                };
            } catch (e) {
                console.warn('[course-group-form] falha ao gerar capa demo', e);
            }
        },
        async save() {
            if (!this.form.name?.trim()) {
                alert(this.t.groups.nameRequired);
                return;
            }
            if (this.form.priceEnabled) {
                const price = Number(this.form.price) || 0;
                const discount = Number(this.form.discount) || 0;
                if (discount < 0 || discount > price) {
                    alert(this.t.groups.discountInvalid);
                    return;
                }
            }
            this.saving = true;
            try {
                const imageUrl = await this.resolveCoverImageUrl();
                const product = toGroupProductPayload({ ...this.form, imageUrl });
                const payload = {
                    ...product,
                    mode: this.form.mode,
                    target: { sectorIds: [], roleIds: [] },
                    // Mantém os cursos já vinculados; gestão de itens fica na tela do grupo
                    items: this.form.items
                        .filter((i) => i.courseId)
                        .map((item, index) => ({
                            courseId: String(item.courseId),
                            order: index + 1,
                            unlockRule: this.form.mode === 'path' && index > 0
                                ? 'previous_completed'
                                : 'always',
                        })),
                };
                let savedId = this.editingId;
                if (this.editingId) {
                    await courseGroupsApi.update(this.editingId, payload, this.token);
                } else {
                    const created = await courseGroupsApi.create(payload, this.token);
                    savedId = created?.id;
                }
                if (savedId) {
                    this.goToDetail(savedId);
                } else {
                    this.$router.push('/dashboard/cursos');
                }
            } catch (e) {
                alert(this.t.common.error);
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.course-group-form-placeholder {
    padding: 2.5rem 1.5rem;
    text-align: center;
}

.corporate-groups-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.corporate-groups-form__access-banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: #eef1fb;
}

.corporate-groups-form__access-banner .corporate-dialog__hint {
    margin: 0;
    flex: 1;
    min-width: 200px;
}

.corporate-groups-form__path-hint {
    margin-top: -0.35rem;
    color: #5b6475;
}

.dashboard.dark .corporate-groups-form__access-banner {
    background: rgba(165, 180, 252, 0.12);
}

.dashboard.dark .corporate-groups-form__path-hint {
    color: #9ca3b4;
}
</style>
